import MilestoneCelebration, {
  useMilestoneDetection,
} from '@/components/learner/MilestoneCelebration';
import StreakWidget from '@/components/learner/StreakWidget';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/api';
import learningStreakService from '@/services/learning-streak.service';
import { businessService } from '@/services/business.service';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  BookOpen,
  Calendar,
  ChevronRight,
  Cloud,
  Flame,
  Moon,
  Play,
  Sun,
  Trophy,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  usePageTracking,
  useTimeTracking,
  useScrollTracking,
} from '@/hooks/useAnalytics';
import { trackEvent } from '@/utils/analytics';
import { LEARNER_DASHBOARD_EVENTS } from '@/constants/analytics.events';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import {
  LEARNER_DASHBOARD_PERMISSION,
  LEARNER_SCHEDULE_PERMISSION,
  LEARNER_COURSES_PERMISSION,
} from '@/constants/constant-permissions';
import { LEARNER_SCHEDULE } from '@/constants/constant-routes';

interface Course {
  id: string;
  title: string;
  thumbnail: string;
  progress: number;
  duration: string;
  nextLesson: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  instructor: string;
  lastAccessed: string;
}

interface Deadline {
  id: string;
  title: string;
  dueDate: string;
  daysLeft: number;
  type: 'mandatory' | 'assignment';
}

interface DashboardStats {
  total_courses: number;
  completed_courses: number;
  in_progress_courses: number;
  not_started_courses: number;
  mandatory_courses: number;
  total_time_spent_hours: number;
  average_progress: number;
}

interface CertificationDashboardStats {
  total_certification: number;
  completed_certification: number;
  in_progress_certification: number;
  mandatory_certification: number;
  total_time_spent_hours: number;
}

export default function LearnerDashboard() {
  // Permission guard
  usePermissionRouteAccess([
    LEARNER_DASHBOARD_PERMISSION.LEARNER_DASHBOARD_READ,
  ]);
  const canViewSchedule = useHasPermission([
    LEARNER_SCHEDULE_PERMISSION.LEARNER_SCHEDULE_READ,
  ]);
  const canWriteCourse = useHasPermission([
    LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE,
  ]);
  const canViewCourse = useHasPermission([
    LEARNER_COURSES_PERMISSION.LEARNER_COURSES_READ,
  ]);
  const canAccessCourses = canViewCourse || canWriteCourse;

  const { user } = useAuth();

  // Analytics hooks
  usePageTracking('/learn/dashboard');
  useTimeTracking('/learn/dashboard');
  useScrollTracking('/learn/dashboard');

  const [timeOfDay] = useState<'morning' | 'afternoon' | 'evening'>(() => {
    const hour = new Date().getHours();
    if (hour < 12) return 'morning';
    if (hour < 17) return 'afternoon';
    return 'evening';
  });

  const learnerName = user?.full_name?.split(' ')[0] || 'Learner';

  // Fetch business settings for company branding
  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000,
  });
  const companyName = businessData?.data?.company_name?.trim() || businessData?.company_name?.trim() || 'Skillzen';

  // Fetch dashboard stats
  const { data: dashboardStats } = useQuery({
    queryKey: ['learner-dashboard-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/course-tracking/dashboard');
        if (response.data.success) {
          return response.data.data as DashboardStats;
        }
        return null;
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        return null;
      }
    },
  });

  // Fetch certification dashboard stats
  const { data: certificationStats } = useQuery({
    queryKey: ['learner-certification-dashboard-stats'],
    queryFn: async () => {
      try {
        const response = await apiClient.get(
          '/api/certification-tracking/dashboard',
        );
        if (response.data.success) {
          return response.data.data as CertificationDashboardStats;
        }
        return null;
      } catch (error) {
        console.error('Failed to fetch certification stats:', error);
        return null;
      }
    },
  });

  // Fetch learning streak data
  const previousStreakRef = useRef<number | undefined>(undefined);
  const { data: streakData } = useQuery({
    queryKey: ['learning-streak-current'],
    queryFn: () => learningStreakService.getCurrentStreak(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 10 * 60 * 1000, // Refetch every 10 minutes
  });

  // Track previous streak value for milestone detection
  useEffect(() => {
    if (streakData) {
      previousStreakRef.current = streakData.current_streak;
    }
  }, [streakData]);

  // Milestone celebration logic
  const { celebratingMilestone, closeCelebration } = useMilestoneDetection(
    streakData?.current_streak || 0,
    previousStreakRef.current,
  );

  // Fetch courses for continue learning
  const { data: coursesData } = useQuery({
    queryKey: ['learner-courses-dashboard'],
    queryFn: async () => {
      try {
        const response = await apiClient.get('/api/course-tracking/my-courses');
        if (response.data.success) {
          return response.data.data;
        }
        return [];
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        return [];
      }
    },
  });

  // Filter in-progress courses for continue learning section
  const continueLearning: Course[] = (coursesData || [])
    .filter((c: any) => c.status === 'IN_PROGRESS')
    .slice(0, 3)
    .map((c: any) => ({
      id: c.course_id,
      title: c.course_title,
      thumbnail: c.thumbnail_image_url ? '🎯' : '📚',
      progress: c.progress_percentage,
      duration: `${c.estimated_duration.total.hours}h ${c.estimated_duration.total.minutes}m left`,
      nextLesson: `Module ${c.completed_modules + 1}`,
      category: c.category || 'General',
      difficulty: c.difficulty || 'intermediate',
      instructor: c.instructor || companyName,
      lastAccessed: c.last_accessed_at
        ? new Date(c.last_accessed_at).toLocaleDateString()
        : 'Recently',
    }));

  // Get upcoming deadlines from courses data
  const upcomingDeadlines: Deadline[] = (coursesData || [])
    .filter(
      (c: any) => c.deadline_date && new Date(c.deadline_date) > new Date(),
    )
    .sort(
      (a: any, b: any) =>
        new Date(a.deadline_date).getTime() -
        new Date(b.deadline_date).getTime(),
    )
    .slice(0, 3)
    .map((c: any) => {
      const daysLeft = Math.ceil(
        (new Date(c.deadline_date).getTime() - new Date().getTime()) /
          (1000 * 60 * 60 * 24),
      );
      return {
        id: c.course_id,
        title: c.course_title,
        dueDate: new Date(c.deadline_date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        daysLeft,
        type: c.is_mandatory ? 'mandatory' : 'assignment',
      };
    });

  const getGreeting = () => {
    const greetings = {
      morning: { text: 'Good morning', icon: <Sun className="w-5 h-5" /> },
      afternoon: {
        text: 'Good afternoon',
        icon: <Cloud className="w-5 h-5" />,
      },
      evening: { text: 'Good evening', icon: <Moon className="w-5 h-5" /> },
    };
    return greetings[timeOfDay];
  };

  const greeting = getGreeting();

  // Track widgets on mount (empty dependency array - fires once)
  useEffect(() => {
    trackEvent(LEARNER_DASHBOARD_EVENTS.WIDGET_QUICK_STATS_VIEW);
    trackEvent(LEARNER_DASHBOARD_EVENTS.WIDGET_CONTINUE_LEARNING_VIEW);
    trackEvent(LEARNER_DASHBOARD_EVENTS.WIDGET_STREAK_VIEW);
    if (upcomingDeadlines.length > 0) {
      trackEvent(LEARNER_DASHBOARD_EVENTS.WIDGET_DEADLINES_VIEW);
    }
  }, []);

  return (
    <>
      {/* Milestone Celebration Modal */}
      {celebratingMilestone && (
        <MilestoneCelebration
          milestone={celebratingMilestone}
          currentStreak={streakData?.current_streak || 0}
          isVisible={true}
          onClose={closeCelebration}
        />
      )}

      <div className="space-y-4 sm:space-y-6">
        {/* Personalized Header */}
        <div className="bg-gradient-to-r from-brand-blue-600 to-brand-purple-600 rounded-xl p-4 sm:p-6 text-white">
          <div className="flex items-start sm:items-center justify-between gap-3 mb-4 sm:mb-6">
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2 flex items-center gap-2 break-words">
                <span className="shrink-0">{greeting.icon}</span>
                <span className="min-w-0">{greeting.text}, {learnerName}!</span>
              </h1>
              <p className="text-sm sm:text-base text-blue-100">
                Ready to continue your learning journey? You're on a{' '}
                {streakData?.current_streak || 0}-day streak! 🔥
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              {/* <Link
              to="/learn/live-chat"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-purple-500 to-brand-pink-500 text-white rounded-lg hover:from-brand-purple-600 hover:to-brand-pink-600 transition-all shadow-lg animate-pulse"
            >
              <Video className="w-5 h-5" />
              <span className="hidden sm:inline">Live AI Chat</span>
            </Link> */}
              <Link
                to={canViewSchedule ? LEARNER_SCHEDULE : '#'}
                className={`flex items-center gap-2 p-2 sm:px-4 sm:py-2 rounded-lg transition-colors ${
                  canViewSchedule
                    ? 'bg-white/20 backdrop-blur-sm hover:bg-white/30'
                    : 'bg-white/10 opacity-50 cursor-not-allowed'
                }`}
                title={
                  !canViewSchedule
                    ? 'You do not have permission to view schedule'
                    : undefined
                }
                onClick={(e) => {
                  if (!canViewSchedule) {
                    e.preventDefault();
                    return;
                  }
                  trackEvent(LEARNER_DASHBOARD_EVENTS.NAVIGATION_SCHEDULE, {
                    from: '/learn/dashboard',
                    to: LEARNER_SCHEDULE,
                  });
                }}
              >
                <Calendar className="w-5 h-5" />
                <span className="hidden sm:inline">Schedule</span>
              </Link>
              {/* <Link
              to="/learn/courses"
              className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
            >
              <Sparkles className="w-5 h-5" />
              <span className="hidden sm:inline">Discover</span>
            </Link> */}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-blue-100 text-xs">Learning Streak</p>
                  <p className="text-2xl font-bold flex items-center gap-1">
                    {streakData?.current_streak || 0}{' '}
                    <Flame className="w-5 h-5 text-orange-300" />
                  </p>
                </div>
              </div>
            </div>
            {/* <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Weekly Goal</p>
                <p className="text-2xl font-bold">0/5h</p>
              </div>
            </div>
            <div className="mt-2 bg-white/20 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2"
                style={{ width: '70%' }}
              />
            </div>
          </div> */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-blue-100 text-xs">Courses Active</p>
                  <p className="text-2xl font-bold">
                    {Number(dashboardStats?.in_progress_courses || 0) +
                      Number(dashboardStats?.not_started_courses || 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 min-w-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-blue-100 text-xs">Total Certifications</p>
                  <p className="text-2xl font-bold flex items-center gap-1">
                    {certificationStats?.total_certification || 0}{' '}
                    <Trophy className="w-5 h-5 text-amber-300" />
                  </p>
                </div>
              </div>
            </div>
            {/* <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-xs">Points Earned</p>
                <p className="text-2xl font-bold">0</p>
              </div>
            </div>
          </div> */}
          </div>
        </div>

        {/* Upcoming Deadlines - Full Width */}
        {upcomingDeadlines.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-600" />
                Upcoming Deadlines
              </h2>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingDeadlines.map((deadline) => (
                  <div
                    key={deadline.id}
                    className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg"
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        deadline.daysLeft <= 5
                          ? 'bg-red-100'
                          : deadline.daysLeft <= 10
                            ? 'bg-yellow-100'
                            : 'bg-blue-100'
                      }`}
                    >
                      <Calendar
                        className={`w-4 h-4 ${
                          deadline.daysLeft <= 5
                            ? 'text-red-600'
                            : deadline.daysLeft <= 10
                              ? 'text-yellow-600'
                              : 'text-blue-600'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 break-words">
                        {deadline.title}
                      </p>
                      <p className="text-xs text-gray-500">
                        {deadline.dueDate}
                      </p>
                      {deadline.type === 'mandatory' && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded">
                          Mandatory
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        deadline.daysLeft <= 5
                          ? 'text-red-600'
                          : deadline.daysLeft <= 10
                            ? 'text-yellow-600'
                            : 'text-gray-600'
                      }`}
                    >
                      {deadline.daysLeft}d
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Continue Learning - 2 columns */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6 min-w-0">
            {/* Continue Learning Section */}
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Play className="w-5 h-5 text-blue-600" />
                    Continue Learning
                  </h2>
                  <Link
                    to={canAccessCourses ? '/learn/courses' : '#'}
                    className={`text-sm flex items-center gap-1 transition
    ${
      canAccessCourses
        ? 'text-brand-blue-600 hover:text-brand-blue-700 cursor-pointer'
        : 'text-gray-400 cursor-not-allowed opacity-60'
    }`}
                    onClick={(e) => {
                      if (!canAccessCourses) {
                        e.preventDefault();
                        return;
                      }

                      trackEvent(
                        LEARNER_DASHBOARD_EVENTS.NAVIGATION_VIEW_ALL_COURSES,
                        {
                          from: '/learn/dashboard',
                          to: '/learn/courses',
                        },
                      );
                    }}
                  >
                    View all <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
                {continueLearning.length > 0 ? (
                  continueLearning.map((course) => (
                    <div
                      key={course.id}
                      className="flex gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <div className="text-2xl sm:text-3xl shrink-0">{course.thumbnail}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-4 mb-2">
                          <div className="min-w-0">
                            <h3 className="font-medium text-gray-900 break-words">
                              {course.title}
                            </h3>
                            <p className="text-sm text-gray-500">
                              Next: {course.nextLesson}
                            </p>
                          </div>
                          <span className="text-xs sm:text-sm text-gray-500 shrink-0">
                            {course.lastAccessed}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mb-2">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-600">
                                {course.progress}% complete
                              </span>
                              {/* <span className="text-xs text-gray-600">{course.duration}</span> */}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-brand-blue-600 h-2 rounded-full"
                                style={{ width: `${course.progress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                        <Link
                          to={
                            canWriteCourse ? `/learn/study/${course.id}` : '#'
                          }
                          className={`w-full sm:w-auto text-center px-4 py-2 sm:py-1.5 rounded-lg text-sm font-medium inline-block transition
    ${
      canWriteCourse
        ? 'bg-brand-blue-600 text-white hover:bg-brand-blue-700 cursor-pointer'
        : 'bg-gray-400 text-white cursor-not-allowed opacity-60'
    }`}
                          onClick={(e) => {
                            if (!canWriteCourse) {
                              e.preventDefault();
                              return;
                            }

                            trackEvent(
                              LEARNER_DASHBOARD_EVENTS.COURSE_RESUME_CLICK,
                              {
                                course_id: course.id,
                                course_title: course.title,
                                progress: course.progress,
                              },
                            );
                          }}
                        >
                          Resume Course
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600">No courses in progress</p>
                    <Link
                      to={canAccessCourses ? '/learn/courses' : '#'}
                      className={`mt-4 inline-block px-4 py-2 rounded-lg transition
    ${
      canAccessCourses
        ? 'bg-brand-blue-600 text-white hover:bg-brand-blue-700 cursor-pointer'
        : 'bg-gray-400 text-white cursor-not-allowed opacity-60'
    }`}
                      onClick={(e) => {
                        if (!canAccessCourses) {
                          e.preventDefault();
                          return;
                        }

                        trackEvent(
                          LEARNER_DASHBOARD_EVENTS.COURSE_EXPLORE_CLICK,
                          {
                            from: '/learn/dashboard',
                            to: '/learn/courses',
                          },
                        );
                      }}
                    >
                      Explore Courses
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Learning Streak Widget */}
            <StreakWidget />
          </div>
        </div>
      </div>
    </>
  );
}
